import { element } from "./dom";

export interface TableColumn {
  readonly label: string;
  readonly cellClassName?: string;
}

export interface TableRow {
  readonly cells: readonly (string | Node)[];
  readonly action?: {
    readonly label: string;
    readonly onSelect: () => void | Promise<void>;
    readonly disabled: boolean;
  };
}

export function table(
  headings: readonly (string | TableColumn)[],
  rows: readonly TableRow[],
): HTMLDivElement {
  const columns = headings.map((column): TableColumn =>
    typeof column === "string" ? { label: column } : column,
  );
  const table = element("table", "data-table");
  table.append(
    element(
      "thead",
      null,
      element(
        "tr",
        null,
        ...columns.map(({ label }) => {
          const cell = element("th", null, label);
          cell.scope = "col";
          return cell;
        }),
      ),
    ),
  );
  table.append(
    element(
      "tbody",
      null,
      ...rows.map(({ cells, action }) => {
        const row = element(
          "tr",
          null,
          ...cells.map((value, index) => {
            const column = columns[index];
            const cell = element("td", column?.cellClassName ?? null, value);
            cell.dataset.label = column?.label ?? "";
            return cell;
          }),
        );
        if (action !== undefined) {
          row.classList.add("data-table__row--clickable");
          row.tabIndex = action.disabled ? -1 : 0;
          row.setAttribute("aria-label", action.label);
          row.setAttribute("aria-disabled", String(action.disabled));
          row.addEventListener("click", (event) => {
            if (
              action.disabled ||
              (event.target instanceof Element &&
                event.target.closest("a, button, input, select, textarea"))
            )
              return;
            void action.onSelect();
          });
          row.addEventListener("keydown", (event) => {
            if (
              action.disabled ||
              event.target !== row ||
              (event.key !== "Enter" && event.key !== " ")
            )
              return;
            event.preventDefault();
            if (!event.repeat) void action.onSelect();
          });
        }
        return row;
      }),
    ),
  );
  return element("div", "table-scroll", table);
}
